// ============================================================
// Kids Entrepreneurship Week — Full Curriculum Data
// Source: KidsEntrepreneurshipWeekCompleteGuide.docx
//         KidsEntrepreneurshipWeekJapanese.docx
// ============================================================

export const DAYS = [
  {
    day: 1,
    weekday: { en: 'Monday', ja: '月曜日' },
    theme: {
      en: 'The Big Idea — What Is Entrepreneurship?',
      ja: 'ビッグアイデア — アントレプレナーって何だろう？',
    },
    goal: {
      en: 'Students understand what an entrepreneur is, identify a real problem around them, and leave with a hand-drawn Business Idea Card — their first business concept.',
      ja: '生徒が「アントレプレナー」とは何かを自分の言葉で説明できるようになり、身近な問題を1つ以上見つけ、「ビジネスアイデアカード」を完成させる。',
    },
    vocab: [
      {
        word: { en: 'ENTREPRENEUR', ja: 'アントレプレナー' },
        def: { en: 'A person who sees a problem and creates something to solve it.', ja: '問題を見つけ、それを解決するものを作り出す人' },
        emoji: '💡',
      },
      {
        word: { en: 'PROBLEM', ja: '問題' },
        def: { en: 'Something that is difficult, annoying, or missing for people.', ja: '人々が感じる不便・不満・不足していること' },
        emoji: '🔍',
      },
      {
        word: { en: 'IDEA', ja: 'アイデア' },
        def: { en: 'A thought about how to make something better or create something new.', ja: '何かをよりよくするための新しい考え' },
        emoji: '✨',
      },
      {
        word: { en: 'CUSTOMER', ja: 'お客様' },
        def: { en: 'The person who will use or buy your product or service.', ja: '商品やサービスを使ったり買ったりする人' },
        emoji: '🤝',
      },
      {
        word: { en: 'BUSINESS', ja: 'ビジネス' },
        def: { en: 'An organized way of solving a problem and earning money from it.', ja: '問題を解決しながらお金を得る仕組み' },
        emoji: '🏢',
      },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:50',
          duration: { en: '20 min', ja: '20分' },
          title: { en: 'Welcome & Icebreaker — Two Truths and a Dream', ja: 'ウォームアップ — 「2つの本当と1つの夢」ゲーム' },
          type: 'game',
          emoji: '🎉',
          content: {
            en: `Arrange chairs or cushions in a circle before students arrive. Greet each student personally at the door. Begin with energy and warmth.\n\nSAY TO STUDENTS:\n"Welcome to Entrepreneurship Week! This week is completely different from regular school. There are no textbooks, no tests, and no wrong answers. By Friday, you will each have your own business idea that you designed yourself. But first — let's meet each other properly. We're going to play a quick game called Two Truths and a Dream."\n\nHOW TO PLAY:\nEach student shares three things: two facts that are completely true about themselves, and one thing they WISH they could invent or build. No one guesses — everyone just listens and reacts. Go around the circle, 30 seconds each.\n\nMODEL IT YOURSELF FIRST:\n"I love Okinawa soba on rainy evenings. I have lived in Okinawa for three years. And my dream invention is a beach bag that automatically cools down to 15 degrees when you zip it shut."\n\n💡 STAFF TIP: For large groups (12+), have students share with the person next to them first, then invite 5–6 volunteers to share with the whole group.`,
            ja: `生徒が来る前に椅子を円形に並べておきます。一人ひとりをドアで迎え、名前を呼んで温かく迎えましょう。\n\n生徒への言葉：\n「アントレプレナーシップ・ウィークへようこそ！今週は普通の学校とは全然違います。テスト・教科書・間違いはありません。金曜日には、みんなが自分のビジネスアイデアを持って帰ります。まず最初にお互いを知り合いましょう。「2つの本当と1つの夢」ゲームをします！」\n\nゲームのやり方：\n自分について本当のこと2つと、「いつか作りたい・発明したい」夢のもの1つを言う。聞いている人は当てようとしない。ただ聞いて反応する。1人30秒、全員が順番に話す。\n\nまずスタッフが見本を見せてください：\n「沖縄そばが大好きです。沖縄に3年住んでいます。そして私の夢の発明は——ファスナーを閉めると自動的に15度まで冷えるビーチバッグです！」\n\n💡 スタッフへのアドバイス： 生徒が12人以上の場合は、まず隣の人と話してもらい、その後5〜6人に全体で発表させると時間を節約できます。`,
          },
        },
        {
          time: '10:50 – 11:15',
          duration: { en: '25 min', ja: '25分' },
          title: { en: 'Mini Lesson — What Is an Entrepreneur?', ja: 'ミニ授業 — アントレプレナーとは何か？' },
          type: 'lesson',
          emoji: '📖',
          content: {
            en: `This is a conversation, not a lecture. Ask questions constantly and wait for real answers.\n\nSAY:\n"Before I tell you what an entrepreneur is — has anyone ever thought: 'Why doesn't this exist?' or 'Someone really should fix this.' Raise your hand if that has ever happened to you."\n\nTake 2–3 answers. Then reveal the definition and write it large on the board:\n\n🎯 ENTREPRENEUR = Problem Spotter + Solution Maker\n\nWalk through three Okinawa-specific examples:\n\n🌊 PROBLEM: Tourists at Okinawa beaches run out of sunscreen and get burned badly.\n→ SOLUTION: A vending machine at the beach entrance selling 200-yen sunscreen packets.\n\n✏️ PROBLEM: Kids at summer school lose track of their pencils and erasers.\n→ SOLUTION: A personalized stamp kit that marks all belongings in under 10 seconds.\n\n🍜 PROBLEM: Visitors cannot read Japanese menus and feel too embarrassed to ask for help.\n→ SOLUTION: A picture menu app you scan with your phone showing photos and English names.\n\nAfter each example ask: "Could this be a real business in Okinawa? Would people pay for this?" Let students debate!`,
            ja: `これは講義ではなく会話です。常に質問を投げかけ、生徒の答えを引き出してください。\n\n「みんなに聞きます——「なんでこれが存在しないんだろう？」とか「これ、もっとよくできるのに」と思ったことがある人、手を挙げて。」\n\n2〜3人の答えを聞いてから、ホワイトボードに大きく書いてください：\n\n🎯 アントレプレナー = 問題を見つける人 ＋ 解決策を作る人\n\n沖縄を舞台にした具体例を3つ説明してください：\n\n🌊 問題：観光客がビーチで日焼け止めを忘れて、ひどい日焼けをしてしまう\n→ 解決策：海水浴場の入口に200円の日焼け止め自販機を設置する\n\n✏️ 問題：夏休みの学校で、同じ見た目の文具が混ざって誰のかわからなくなる\n→ 解決策：10秒で名前を押せる個人スタンプセットを販売する\n\n🍜 問題：外国人観光客が日本語メニューを読めず注文できない\n→ 解決策：写真と英語で書かれたメニューアプリを作る\n\n「これら全部、沖縄で本当に役立つビジネスになる可能性があります！」`,
          },
        },
        {
          time: '11:15 – 11:45',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Activity — Problem Spotters', ja: 'アクティビティ — 問題スポッター' },
          type: 'activity',
          emoji: '🔎',
          content: {
            en: `Students practise the first skill of entrepreneurship: noticing problems in the world around them. No solutions yet — only observations.\n\nMATERIALS NEEDED:\n• 1 sheet of plain paper per student\n• Pens and pencils\n• Markers or colored pens (optional)\n\nSAY:\n"Right now your only job is to be a Problem Spotter. Forget solutions — we get to those later. I want you to find three problems. Real things that are annoying, difficult, unfair, or missing. Think about your morning, your school, the beach, the shops, anywhere in Okinawa. What makes you think 'ugh, why is this so hard?' Write down three of them."\n\nPrompts for anyone stuck:\n• "What was annoying in your morning today?"\n• "What do your parents complain about at home?"\n• "Is there anything at the beach or in shops that could be better?"\n• "Think about younger kids — what problems do they have?"\n\n💡 STAFF TIP: If a student writes a solution instead of a problem, guide them: "Great — but what is the PROBLEM that made you think of that?"`,
            ja: `アントレプレナーの最初のスキル「問題を見つける力」を練習します。解決策はまだ考えません。観察だけです。\n\n必要な材料：\n・ A4白紙（1人1枚）\n・ 鉛筆・ペン\n・ カラーマーカー（任意）\n\n「今のあなたの仕事は「問題スポッター」です。解決策は後回し！今日は問題を3つ見つけてください。毎日の生活の中で「いやだな」「不便だな」「なんでこうなの？」と思うことを書き出しましょう。学校・家・ビーチ・お店・沖縄のどこでもOKです。」\n\nヒント（考えが浮かばない生徒に使ってください）：\n・「今朝、何か嫌なことやめんどくさいことはあった？」\n・「お父さんやお母さんがよく文句を言うことは何？」\n・「ビーチやお店で「もっとよくなればいいのに」と思うことは？」\n・「もっと小さい子どもが困っていることは何だろう？」\n\n💡 スタッフへのアドバイス： 解決策を書いてしまった生徒には：「それはいいアイデアだね！でも、そのアイデアを思いついた"問題"は何だった？」`,
          },
        },
        {
          time: '11:45 – 11:55',
          duration: { en: '10 min', ja: '10分' },
          title: { en: 'Share Out', ja: '発表タイム' },
          type: 'share',
          emoji: '🗣️',
          content: {
            en: `Ask 3–4 students to share their favourite problem. After each one, ask the whole class:\n"Who else has had this exact problem? Raise your hand."\n\nWhen many hands go up, say:\n"That right there is a business opportunity — a problem that lots of people share."`,
            ja: `3〜4人に一番好きな問題を発表させます。その後クラス全体に聞きます：\n「同じ問題を感じたことがある人、手を挙げて！」\n\nたくさん手が上がるほど、ビジネスチャンスが大きいことを伝えましょう。`,
          },
        },
        {
          time: '11:55 – 12:00',
          duration: { en: '5 min', ja: '5分' },
          title: { en: '⭐ Morning Stamp Time', ja: '⭐ 午前スタンプタイム' },
          type: 'stamp',
          emoji: '⭐',
          content: {
            en: `Give a stamp to every student who completed their Problem Spotter sheet.\n\n"Amazing work this morning! You are now officially Problem Spotters. After lunch we are going to take one of those problems and turn it into your very first business idea. Keep your sheet safe!"`,
            ja: `問題スポッターシートを完成させた全員にスタンプを渡す。\n\n「すごい！みんな本物の問題スポッターになりました。昼食後は、その問題の一つをビジネスアイデアに変えます。シートをなくさないでね！」`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 13:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: 'Re-Energize & Recap', ja: 'エネルギー充電 & 復習' },
          type: 'game',
          emoji: '⚡',
          content: {
            en: `Students' energy drops after lunch. Start standing up. Do a group clap rhythm: two slow claps, four fast claps, everyone shouts "Let's go!" Repeat three times.\n\nThen ask three fast questions while everyone is still standing:\n• "Who can tell me — what is an entrepreneur? Shout it out!"\n• "What problems did we find this morning? Who remembers a great one?"\n• "Take out your Problem Spotter sheet — show it to the person next to you."`,
            ja: `昼食後はエネルギーが下がります。立って始めましょう。グループで10秒間のリズム拍手（2回ゆっくり→4回速く→全員で「ゴー！」）を3回繰り返します。\n\n・「アントレプレナーって何？大きな声で言って！」\n・「今朝見つけた問題で、誰か覚えているものある？」\n・「自分のシートを取り出して、隣の人に見せよう」`,
          },
        },
        {
          time: '13:45 – 14:15',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Mini Lesson — From Problem to Business Idea', ja: 'ミニ授業 — 問題からビジネスアイデアへ' },
          type: 'lesson',
          emoji: '📖',
          content: {
            en: `"This morning you were Problem Spotters. Now you become Idea Makers. A business idea is not a random thought — it is a solution designed for a real problem that real people have. The best ideas are simple. Simple things are easier to build, easier to explain, and easier to sell."\n\nShow this worked example step by step on the board:\n\n🔴 THE PROBLEM: Kids at Okinawa beaches forget sunscreen and get badly burned.\n👥 WHO HAS IT: Families with young children, tourists, school groups.\n💡 THE IDEA: A vending machine at the beach entrance selling single-use sunscreen at 200 yen each.\n✅ WHY IT WORKS: Right there when they need it. Cheap. Fast. No need to go back to a distant shop.\n🏷️ BUSINESS NAME: SunSafe Okinawa\n\n"Notice: this idea is a simple vending machine. Not a high-tech app. Not a factory. Just the right thing, in the right place, at the right time. That is enough to be a real business."`,
            ja: `「午前は問題スポッターでした。午後はアイデアメーカーになります。ビジネスアイデアは「なんとなく思いついたこと」ではありません。本当に困っている人のために作られた解決策です。シンプルなほどいい。シンプルなものほど、実際に作れて、説明できて、売ることができます。」\n\nホワイトボードにこの例を書いて、一緒に考えてください：\n\n🔴 問題：沖縄のビーチで日焼け止めを忘れた家族が、真夏に肌を焼いてしまう\n👥 誰が困ってる？：子連れの家族・観光客・学校の遠足グループ\n💡 アイデア：海水浴場の入口に、100〜200円の使い切り日焼け止め自販機を置く\n✅ なぜ買う？：必要なその場所に・手頃な価格で・すぐ手に入る\n🏷️ ビジネス名：サンセーフ沖縄\n\n「注目してください——これは自販機一台です。ハイテクなアプリでも工場でもない。正しい場所に・正しいタイミングで・必要なものを置く。それだけで立派なビジネスです！」`,
          },
        },
        {
          time: '14:15 – 15:25',
          duration: { en: '70 min', ja: '70分' },
          title: { en: 'Main Activity — My First Business Idea Card', ja: 'メインアクティビティ — 私のビジネスアイデアカード' },
          type: 'activity',
          emoji: '🃏',
          content: {
            en: `This is the most important activity of the day. The Business Idea Card students create here will be developed further every day this week and becomes the basis of their Friday pitch.\n\nMATERIALS NEEDED:\n• 2 sheets of plain A4 paper per student or group\n• Pens and pencils\n• Coloured markers — encourage bold, colourful drawings\n\nSAY:\n"Take one problem from your Problem Spotter sheet — the one you care about most. You are going to invent a business idea that solves it. Fill in every part of the card below. Then on your second sheet, draw your product or service. Make it look as real as possible. Give it a name. Give it a logo."\n\nGuiding questions while walking around:\n• "Who exactly would buy this? Can you describe that person for me?"\n• "Where would someone find your business?"\n• "What is the single most important thing your idea does for the customer?"\n• "Does it have a name yet? What would make a great name?"\n• "If your best friend saw this, would they want it? Why?"\n\n💡 STAFF TIP: If a student finishes early, ask them to create a slogan, add colour to the logo, or think about what makes their idea different from anything that already exists.`,
            ja: `今週最も重要なアクティビティです。今日作るカードは火・水・木曜日に発展させ、金曜日の発表の土台になります。\n\n必要な材料：\n・ 月曜午前のシート\n・ A4白紙2枚（書く用＋絵を描く用）\n・ ペン・鉛筆\n・ カラーマーカー\n\n「問題スポッターシートから一番気になる問題を1つ選んでください。その問題を解決するビジネスアイデアを考えます。カードの全部の欄を埋めて、2枚目の紙に商品やサービスの絵を描いてください。名前もロゴも考えてみましょう。本物のように作ってください！」\n\n歩き回りながら使う質問：\n・「それを買うのはどんな人？その人のことを教えて」\n・「お客様はどこでこのビジネスを見つけるの？」\n・「このアイデアの一番大事なことは何？」\n・「名前は決まった？どんな名前がいいと思う？」\n\n💡 スタッフへのアドバイス： 早く終わった生徒には：ロゴの色を追加する・キャッチコピーを考える、などを提案してください。`,
          },
        },
        {
          time: '15:25 – 15:50',
          duration: { en: '25 min', ja: '25分' },
          title: { en: 'Mini Pitch Practice', ja: 'ミニピッチ練習' },
          type: 'activity',
          emoji: '🎤',
          content: {
            en: `30 seconds per student or group. They say three things: what the idea is, who it is for, and why people will love it. They can read directly from their card — that is fine. Model it yourself first.\n\nAfter each pitch, give one specific piece of praise:\n• "I loved how clearly you described your customer."\n• "That problem is so relatable — I think lots of people in Okinawa feel that way."\n• "Your business name is very memorable — I won't forget it."\n\n💡 STAFF TIP: If a student refuses to speak, do not force it. Let them pass. They still get a stamp for completing their Business Idea Card.`,
            ja: `1人30秒。「私のアイデアは○○です。○○な人のために作りました。なぜなら○○だからです」の3つを言うだけ。カードを読んでいい。まずスタッフが見本を見せてください。\n\n各発表後に具体的な褒め言葉を1つ言ってください：\n・「お客様の説明がとてもわかりやすかった」\n・「その問題、すごく多くの人が感じていると思う」\n・「ビジネス名が覚えやすくていいね」\n\n💡 スタッフへのアドバイス： 発表を拒否する生徒は無理に立たせないでください。カードを完成させていればスタンプを渡します。`,
          },
        },
        {
          time: '15:50 – 16:00',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '⭐ Afternoon Stamp & Day Close', ja: '⭐ 午後スタンプ & 今日のまとめ' },
          type: 'stamp',
          emoji: '⭐',
          content: {
            en: `Give an afternoon stamp to everyone who completed their Business Idea Card and joined the pitch.\n\nCollect all Business Idea Cards before students leave — keep them safe for Tuesday. Close the day with a standing circle: each student says ONE word for how they feel right now.\n\n"Today you became Problem Spotters AND Idea Makers. Tomorrow we learn about the most important person in any business — your customer. See you tomorrow!"`,
            ja: `ビジネスアイデアカードを完成させ、ピッチに参加した全員にスタンプを渡す。\n\n生徒が帰る前にカードを全部回収してください（火曜日に返します）。\n\n「今日みなさんは問題スポッターとアイデアメーカーになりました。明日は自分のビジネスで最も大切な人——お客様——について深く学びます。また明日！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'day1-problem-spotter',
        title: { en: 'MY PROBLEM SPOTTER SHEET', ja: '問題スポッターシート' },
        emoji: '🔎',
        fields: [
          { type: 'header', label: { en: 'PROBLEM 1', ja: '問題１' } },
          { type: 'line', label: { en: 'Describe the problem:', ja: '問題の内容：' } },
          { type: 'line', label: { en: 'Where does this happen?', ja: 'それはどこで起きる？' } },
          { type: 'line', label: { en: 'Who does it affect?', ja: '誰が困っている？' } },
          { type: 'header', label: { en: 'PROBLEM 2', ja: '問題２' } },
          { type: 'line', label: { en: 'Describe the problem:', ja: '問題の内容：' } },
          { type: 'line', label: { en: 'Where does this happen?', ja: 'それはどこで起きる？' } },
          { type: 'line', label: { en: 'Who does it affect?', ja: '誰が困っている？' } },
          { type: 'header', label: { en: 'PROBLEM 3', ja: '問題３' } },
          { type: 'line', label: { en: 'Describe the problem:', ja: '問題の内容：' } },
          { type: 'line', label: { en: 'Where does this happen?', ja: 'それはどこで起きる？' } },
          { type: 'line', label: { en: 'Who does it affect?', ja: '誰が困っている？' } },
          { type: 'choice', label: { en: 'MY FAVOURITE PROBLEM (circle one):', ja: '一番好きな問題（○で囲む）：' }, options: ['1', '2', '3'] },
          { type: 'draw', label: { en: 'DRAW YOUR FAVOURITE PROBLEM HERE:', ja: '一番好きな問題の絵を描こう：' } },
        ],
      },
      {
        id: 'day1-business-idea',
        title: { en: 'MY FIRST BUSINESS IDEA CARD', ja: '私のビジネスアイデアカード' },
        emoji: '🃏',
        fields: [
          { type: 'line', label: { en: 'THE PROBLEM I AM SOLVING:', ja: '解決したい問題：' } },
          { type: 'multiline', label: { en: 'MY IDEA (what I will make or do to solve it):', ja: '私のアイデア（何を作る・何をする？）：' } },
          { type: 'line', label: { en: 'WHO IS THIS FOR? (describe your customer):', ja: '誰のため？（どんな人がお客様？）：' } },
          { type: 'line', label: { en: 'WHERE WOULD PEOPLE FIND MY BUSINESS?', ja: 'どこで見つけてもらえる？（お店・ビーチ・オンラインなど）：' } },
          { type: 'line', label: { en: 'WHY WILL PEOPLE LOVE IT?', ja: 'なぜお客様は気に入ってくれる？：' } },
          { type: 'line', label: { en: 'MY BUSINESS NAME:', ja: 'ビジネスの名前（まだなくてもOK）：' } },
          { type: 'draw', label: { en: 'DRAW YOUR IDEA / LOGO HERE:', ja: 'アイデアの絵・ロゴを描こう：' } },
        ],
      },
    ],
  },

  // ── DAY 2 ──────────────────────────────────────────────────
  {
    day: 2,
    weekday: { en: 'Tuesday', ja: '火曜日' },
    theme: {
      en: 'Know Your Customer — Who Are You Building This For?',
      ja: 'お客様を知ろう — 誰のために作っているの？',
    },
    goal: {
      en: 'Students develop a clear picture of their customer — a specific, real person with specific needs. They practise customer interviews through roleplay and use what they learn to sharpen their Business Idea Card.',
      ja: 'お客様が誰なのかを具体的に描き、ロールプレイを通じてお客様の本音を理解する。得た気づきを元にビジネスアイデアを改善する。',
    },
    vocab: [
      {
        word: { en: 'CUSTOMER', ja: 'お客様' },
        def: { en: 'The person who buys or uses your product or service.', ja: '商品やサービスを使う・買う人' },
        emoji: '🤝',
      },
      {
        word: { en: 'NEED', ja: 'ニーズ' },
        def: { en: 'Something a person truly requires — not just wants, but genuinely needs.', ja: 'お客様が本当に必要としていること' },
        emoji: '💭',
      },
      {
        word: { en: 'EMPATHY', ja: '共感' },
        def: { en: 'Understanding how another person feels and what they experience.', ja: '相手の気持ちや体験を理解しようとすること' },
        emoji: '❤️',
      },
      {
        word: { en: 'FEEDBACK', ja: 'フィードバック' },
        def: { en: "What someone tells you about your idea — helps you make it better.", ja: 'アイデアをよくするために相手からもらう意見' },
        emoji: '💬',
      },
      {
        word: { en: 'TARGET CUSTOMER', ja: 'ターゲット顧客' },
        def: { en: 'The specific type of person most likely to use your business.', ja: '自分のビジネスを最も使いそうな特定の人' },
        emoji: '🎯',
      },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:48',
          duration: { en: '18 min', ja: '18分' },
          title: { en: 'Energizer Game — "Yes, And..."', ja: 'エナジャイザーゲーム — 「そうだね、そして…」' },
          type: 'game',
          emoji: '🎮',
          content: {
            en: `PURPOSE: Trains students to listen deeply and build on ideas — the core skill for understanding customers.\n\nHOW TO PLAY:\n1. Students pair up and stand facing each other.\n2. One student makes a statement about a made-up business idea.\n   Example: "My business sells shoes that glow in the dark."\n3. The partner must respond with "Yes, AND..." and add something new.\n   Example: "Yes, and they charge wirelessly when you walk on the beach!"\n4. Keep going for 2 minutes — each person alternates "Yes, and..."\n5. The rule: you CANNOT say "No", "But", or "Actually..."\n\nDEBRIEF:\n• "How did it feel when your partner said Yes?"\n• "How would it feel if they said No instead?"\n• "What does this teach us about how to treat our customers' needs?"`,
            ja: `目的：相手の話を最後まで聞き、受け入れて発展させるスキルを楽しく練習する\n\nやり方：\n1. 2人1組になって向き合う\n2. 1人目が架空のビジネスアイデアを1文で言う\n   例：「光るスニーカーを売るビジネスを始めます」\n3. もう1人は必ず「そうだね、そして…」で続ける\n   例：「そうだね、そしてビーチで歩くと砂が光るんだね！」\n4. 交互に「そうだね、そして…」で2分間続ける\nルール：「でも」「いや」「それは無理」は絶対禁止！\n\nふりかえり質問：\n・「"そうだね"と言われたとき、どんな気持ちだった？」\n・「"でも"と言われたらどうなると思う？」\n・「お客様と話すときも同じことが大切なのはなぜ？」`,
          },
        },
        {
          time: '10:48 – 11:10',
          duration: { en: '22 min', ja: '22分' },
          title: { en: 'Mini Lesson — Who Is a Customer?', ja: 'ミニ授業 — お客様とは誰か？' },
          type: 'lesson',
          emoji: '📖',
          content: {
            en: `Return Business Idea Cards from Monday. Ask: "Who is this idea for?" Students often say "everyone" or "kids." Push deeper!\n\n"Every great business knows EXACTLY who their customer is. Not 'everyone.' A specific person. A tourist family with two young children on a hot August day. The more clearly you can picture your customer, the better your idea becomes."\n\n📋 CUSTOMER PROFILE FRAMEWORK:\n• Who are they? — Age, where they live, what they do day to day\n• What do they want? — The outcome or result they are hoping for\n• What do they fear? — What problem or feeling they want to avoid\n• Where are they? — Online, at the beach, at school?\n• Why would they choose you? — What makes your idea better than doing nothing?`,
            ja: `月曜日のビジネスアイデアカードを返却します。「誰のための商品ですか？」と聞きましょう。よくある答えは「みんな」です。でも「みんな」を対象にしたビジネスは、誰にも刺さりません。\n\n「素晴らしいビジネスは、お客様を超具体的に知っています。「みんな」ではなく——「真夏の沖縄ビーチに来た、2人の子連れの観光客家族」のように。お客様を具体的に想像できるほど、アイデアはどんどん良くなります。」\n\n📋 お客様プロフィールの質問：\n・どんな人？ — 年齢・性別・どこに住んでいて、毎日何をしている人？\n・何が欲しい？ — その人が求めている結果・体験は何？\n・何を嫌がる？ — 避けたい問題・感情は何？\n・どこにいる？ — オンライン・ビーチ・学校…どこで見つけられる？\n・なぜ選ぶ？ — 他の選択肢ではなく、あなたのビジネスを選ぶ理由は？`,
          },
        },
        {
          time: '11:10 – 11:48',
          duration: { en: '38 min', ja: '38分' },
          title: { en: 'Activity — Customer Profile Card', ja: 'アクティビティ — お客様プロフィールカード' },
          type: 'activity',
          emoji: '👤',
          content: {
            en: `Students create a detailed profile of the specific person their business is designed for. Encourage them to give this person a name and draw a simple face — this makes the customer feel real.\n\nMATERIALS NEEDED:\n• Business Idea Cards from Monday\n• 1 sheet of paper per student or group\n• Coloured pens and markers\n\n💡 STAFF TIP: If a student writes "everyone," ask: "If you could only sell to ONE person, who would it be? Describe them for me."`,
            ja: `お客様に名前をつけて、顔を描いてみましょう。抽象的な「誰か」ではなく、実在する一人の人物として考えることで、より深いアイデアが生まれます。\n\n必要な材料：\n・ 月曜日のビジネスアイデアカード\n・ A4白紙1枚\n・ カラーペン・マーカー\n\n💡 スタッフへのアドバイス： 「お客様はみんな」と書いた生徒には：「もし1人だけにしか売れないとしたら、誰に売りたい？その人を描写して」と聞いてください。`,
          },
        },
        {
          time: '11:48 – 12:00',
          duration: { en: '12 min', ja: '12分' },
          title: { en: '⭐ Share Out & Morning Stamp', ja: '⭐ 発表タイム & 午前スタンプ' },
          type: 'stamp',
          emoji: '⭐',
          content: {
            en: `Ask 3 students to introduce their customer in one sentence. Ask the class: "Does this customer feel real?"\n\nGive a stamp to everyone who completed their Customer Profile Card.\n\n"This afternoon you will actually TALK to that customer — your classmates will play the role. Prepare your questions!"`,
            ja: `3人にお客様を1文で紹介させます。クラスに「このお客様、リアルに感じる？」と聞きましょう。\n\nお客様プロフィールカードを完成させた全員にスタンプを渡す。\n\n「午後はそのお客様と実際に話します——クラスメートが演じますが！お客様インタビューの準備をしておいてね。」`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 13:42',
          duration: { en: '12 min', ja: '12分' },
          title: { en: 'Energizer — Human Bingo', ja: 'エナジャイザー — 人間ビンゴ' },
          type: 'game',
          emoji: '🎲',
          content: {
            en: `PURPOSE: Warm-up for customer interview skills — asking questions, listening, verifying.\n\nHOW TO PLAY:\n1. Each person draws a 3×3 grid (9 squares) on paper.\n2. Write something that might be true about a classmate in each square.\n   Examples: "Likes spicy food" / "Has a younger sibling" / "Listens to music every day"\n3. Walk around and ask classmates — if it's true, they sign that square.\n4. First to get 3 in a row (horizontal, vertical, diagonal) shouts "BINGO!"\n\nDEBRIEF: "To win bingo you had to ask good questions and really listen. That's exactly what customer interviews need."`,
            ja: `目的：質問する・聞く・確認するという、お客様調査の基本動作を楽しく練習する\n\nやり方：\n1. 各自が紙に3×3のマス（9マス）を書く\n2. 各マスにクラスメートに当てはまりそうなことを書く\n   例：「辛い食べ物が好き」「弟か妹がいる」「音楽をスマホで毎日聴く」\n3. 立ち歩いてクラスメートに質問し、当てはまる人にサインをもらう\n4. 縦・横・斜めのどれか3マス揃ったら「ビンゴ！」と叫ぶ\n\nふりかえり：「ビンゴを達成するには、うまく質問して、答えをちゃんと聞く必要がありましたね。それがまさにお客様インタビューで必要なスキルです。」`,
          },
        },
        {
          time: '13:42 – 14:30',
          duration: { en: '48 min', ja: '48分' },
          title: { en: 'Activity — Customer Interview Roleplay', ja: 'アクティビティ — お客様インタビューロールプレイ' },
          type: 'activity',
          emoji: '🎭',
          content: {
            en: `Half the students play "entrepreneurs," the other half play "customers" using role cards. Switch after 20 minutes.\n\nCUSTOMER ROLE CARDS:\n🅐 Busy parent who loves convenience — pays only for things that save time and reduce stress.\n🅑 13-year-old who loves tech and gaming — weekly allowance is 1,000 yen, only buys "super cool" things.\n🅒 First-time tourist to Okinawa — can't speak Japanese, wants to enjoy beach and local food.\n🅓 Grandparent — not comfortable with smartphones, trusts recommendations from friends.\n\nEntrepreneurs MUST ask these 3 questions:\n• "Does this idea solve a problem you actually have?"\n• "What would make this idea even better?"\n• "Would you pay for this? Why or why not?"\n\n💡 STAFF TIP: If roleplay feels stiff at first, play the customer role yourself for the first minute. Once students see it, they'll start naturally.`,
            ja: `半分が「アントレプレナー」、半分が「お客様」役を演じます。20分後に役を交代します。\n\nお客様ロールカード：\n🅐 忙しい子育て中の親。便利なものは好きだが、複雑なものは嫌い。時間とストレスを減らすものにしかお金を払わない。\n🅑 テクノロジーとゲームが大好きな13歳。1週間のお小遣いは1,000円。「超クール」なものにしか使わない。\n🅒 沖縄を初めて訪れた観光客。日本語が話せない。ビーチや地元の食べ物を楽しみたい。\n🅓 おじいちゃん・おばあちゃん。スマホは苦手。シンプルで信頼できるものが好き。\n\nアントレプレナーが必ず聞く3つの質問：\n・「このアイデアは、あなたが本当に感じている問題を解決していますか？」\n・「このアイデアをもっとよくするには、何を変えればいいですか？」\n・「これにお金を払いますか？なぜですか？なぜ払わないですか？」`,
          },
        },
        {
          time: '14:30 – 15:25',
          duration: { en: '55 min', ja: '55分' },
          title: { en: 'Activity — Update Your Idea Card (Version 2)', ja: 'アクティビティ — アイデアカードをアップデート（バージョン2）' },
          type: 'activity',
          emoji: '✏️',
          content: {
            en: `"You listened to your customer. Now use what you heard to improve your idea. The best entrepreneurs always listen to customers and change their ideas. Changing is not weakness — it's getting smarter."\n\nMATERIALS:\n• Idea Card from Monday\n• 1 new sheet of A4 (for Version 2)\n• Pens and coloured markers\n\n💡 STAFF TIP: For students afraid to change: "It's completely OK if Monday's idea changes. That's the whole point of this week."`,
            ja: `「お客様の声を聞きました。今度はそれを使ってアイデアを改善します。一番良いアントレプレナーはいつもお客様の話を聞いてアイデアを変えます。変えることは弱さじゃない——賢くなっているということです。」\n\n必要な材料：\n・ アイデアカード（月曜日版）\n・ A4白紙1枚（バージョン2用）\n・ ペン・カラーマーカー\n\n💡 スタッフへのアドバイス： 変更することを恐れている生徒には：「月曜日のアイデアが変わっても全然OK。それが今週の目的です」と伝えてください。`,
          },
        },
        {
          time: '15:50 – 16:00',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '⭐ Afternoon Stamp & Day Close', ja: '⭐ 午後スタンプ & 今日のまとめ' },
          type: 'stamp',
          emoji: '⭐',
          content: {
            en: `Give a stamp to everyone who updated their Idea Card and participated in the roleplay.\n\n"Today you thought about business from your customer's eyes. Tomorrow is BUILD day — we have spaghetti, marshmallows, and a challenge waiting. Bring your energy!"`,
            ja: `アイデアカードを更新し、ロールプレイに参加した全員にスタンプを渡す。\n\n「今日はお客様の目線でビジネスを考えました。明日は実際に「作る」日です。スパゲッティと……マシュマロを使ったチャレンジがありますよ。エネルギーを持ってきてね！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'day2-customer-profile',
        title: { en: 'MY CUSTOMER PROFILE CARD', ja: 'お客様プロフィールカード' },
        emoji: '👤',
        fields: [
          { type: 'line', label: { en: "My Customer's Name (make one up!):", ja: 'お客様の名前（架空でOK）：' } },
          { type: 'line', label: { en: 'Age:', ja: '年齢：' } },
          { type: 'line', label: { en: 'Where do they live?', ja: 'どこに住んでいる？：' } },
          { type: 'line', label: { en: 'What do they do every day?', ja: '毎日何をしている？：' } },
          { type: 'multiline', label: { en: 'WHAT DOES THIS PERSON WANT?', ja: 'この人が一番欲しいもの・求めていること：' } },
          { type: 'multiline', label: { en: 'WHAT DO THEY FEAR OR DISLIKE?', ja: 'この人が避けたいこと・嫌いなこと：' } },
          { type: 'line', label: { en: 'WHERE CAN I FIND THEM?', ja: 'どこでこの人に会える？（ビーチ・学校・ショッピングモール・オンラインなど）：' } },
          { type: 'line', label: { en: 'WHY WILL THEY CHOOSE MY BUSINESS?', ja: 'なぜこの人は私のビジネスを選ぶ？：' } },
          { type: 'draw', label: { en: 'DRAW YOUR CUSTOMER HERE:', ja: 'お客様の顔・様子を描こう：' } },
        ],
      },
      {
        id: 'day2-idea-update',
        title: { en: 'IDEA UPDATE SHEET — VERSION 2', ja: 'アイデア アップデートシート（バージョン２）' },
        emoji: '✏️',
        fields: [
          { type: 'multiline', label: { en: 'WHAT I HEARD FROM MY CUSTOMER:', ja: 'お客様から聞いたこと：' } },
          { type: 'multiline', label: { en: 'WHAT I CHANGED IN MY IDEA:', ja: 'アイデアを変えた点：' } },
          { type: 'multiline', label: { en: 'WHAT I KEPT (because customers liked it):', ja: 'そのまま残したこと（お客様が気に入っていたから）：' } },
          { type: 'multiline', label: { en: 'MY UPDATED BUSINESS (describe in 2 sentences):', ja: 'アップデートしたビジネス説明（2文で）：' } },
        ],
      },
    ],
  },

  // ── DAY 3 ──────────────────────────────────────────────────
  {
    day: 3,
    weekday: { en: 'Wednesday', ja: '水曜日' },
    theme: {
      en: 'Build It & Brand It — Design Your Product',
      ja: '作って・ブランドにしよう — 商品をデザインする',
    },
    goal: {
      en: 'Students experience the challenge of physically building something under constraints (Spaghetti Tower Challenge), then apply those lessons to designing their own product, creating a brand name, logo, and visual.',
      ja: 'スパゲッティタワーチャレンジでチームワークと「作って・失敗して・改善する」サイクルを体験。その後、自分の商品・サービスをデザインし、ロゴ・ポスター・プロトタイプを作る。',
    },
    vocab: [
      {
        word: { en: 'PROTOTYPE', ja: 'プロトタイプ' },
        def: { en: 'A first rough version of a product — not perfect, just enough to test the idea.', ja: '完成品ではない、アイデアを確かめるための最初のバージョン' },
        emoji: '🔧',
      },
      {
        word: { en: 'BRAND', ja: 'ブランド' },
        def: { en: 'The name, look, and feeling that makes a business recognizable and memorable.', ja: 'ビジネスの名前・見た目・雰囲気のすべて' },
        emoji: '🏷️',
      },
      {
        word: { en: 'LOGO', ja: 'ロゴ' },
        def: { en: 'A simple image or symbol that represents a business at a glance.', ja: 'ビジネスを一目でわかる絵やマーク' },
        emoji: '🎨',
      },
      {
        word: { en: 'SLOGAN', ja: 'スローガン' },
        def: { en: 'A short, catchy phrase that captures what a business stands for.', ja: 'ビジネスの魅力を伝える短いキャッチフレーズ' },
        emoji: '💬',
      },
      {
        word: { en: 'DESIGN', ja: 'デザイン' },
        def: { en: 'Making deliberate choices about how something looks, feels, and works.', ja: '見た目・使いやすさについて意識的に選択すること' },
        emoji: '✏️',
      },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 11:05',
          duration: { en: '35 min', ja: '35分' },
          title: { en: 'GAME — Spaghetti Tower Challenge', ja: 'ゲーム — スパゲッティタワーチャレンジ' },
          type: 'game',
          emoji: '🍝',
          content: {
            en: `PURPOSE: Teaches prototyping, teamwork, fast decision-making, and learning from failure — all core entrepreneurship skills.\n\nWHAT EACH TEAM GETS:\n• 20 sticks of dry spaghetti\n• 1 metre of masking tape\n• 1 metre of string\n• 1 marshmallow (or small soft sweet)\n\nTHE CHALLENGE:\nBuild the TALLEST free-standing tower you can in 18 minutes. The marshmallow MUST be on the very top. The tower must stand on its own for 5 seconds.\n\nRULES:\n• The marshmallow cannot be supported underneath — it must sit on the tip\n• You can break the spaghetti\n• The tower must be fully free-standing\n\nDuring the build (say every 5 minutes):\n• "6 minutes left — is your marshmallow going to make it to the top?"\n• "3 minutes — start stabilizing!"\n• "Time! Hands off — which towers are standing?"\n\nDEBRIEF:\n• "What did your team disagree about? How did you decide?"\n• "Did anyone change their plan partway through? Why?"\n• "If you had 5 more minutes, what would you change?"\n\n🎯 KEY MESSAGE: "In business, your first idea never works perfectly. What matters is trying fast, failing fast, and improving fast."`,
            ja: `目的：作って・失敗して・改善する体験を通じ、アントレプレナーシップ思考を身体で学ぶ\n\n各チームへの支給品：\n・乾燥スパゲッティ 20本\n・マスキングテープ 1メートル\n・ひも 1メートル\n・マシュマロ（または小さいグミ）1個\n\nチャレンジ内容：\n18分以内に、マシュマロを頂点に乗せた「一番高い自立するタワー」を作る。タワーは誰も触れない状態で5秒以上立ち続けなければならない。\n\n途中の声かけ：\n・「残り6分！マシュマロはてっぺんに乗りそう？」\n・「残り3分！安定させることを考えよう！」\n・「時間！手を離して——どのタワーが立っている？」\n\nふりかえり：\n・「チームで何について話し合った？何で揉めた？」\n・「途中で計画を変えた人いる？なぜ変えたの？」\n・「あと5分あったら何を変える？」\n\n🎯 重要メッセージ：「ビジネスでも最初のアイデアは必ずうまくいかない。大切なのは早く試して、早く改善することです。」`,
          },
        },
        {
          time: '11:05 – 11:25',
          duration: { en: '20 min', ja: '20分' },
          title: { en: 'Mini Lesson — What Makes a Good Product?', ja: 'ミニ授業 — 良い商品とは何か？' },
          type: 'lesson',
          emoji: '📖',
          content: {
            en: `"The challenge you just did showed you — what works on paper and what works in reality are very different. Today you will design your idea as a real product or service.\n\nA great product is:\n✅ USEFUL — clearly solves the problem. Customers get what they need.\n✅ SIMPLE — you don't need to explain it. It's not complicated to use.\n✅ MEMORABLE — the name and look stick in your head. You'd tell a friend.\n\nIf all three are true, people will buy it, remember it, and tell their friends. That's how a business grows."`,
            ja: `「さっきのチャレンジで体験しましたね——紙の上のアイデアと、実際に作るものは全く違います。今日は自分のアイデアを商品・サービスとして形にします。\n\n良い商品の3つの条件：\n✅ 役に立つ — 問題をはっきり解決する。お客様が必要なものを得られる。\n✅ シンプル — 説明しなくてもわかる。使い方が複雑でない。\n✅ 覚えられる — 名前や見た目が頭に残る。友達に話したくなる。\n\n「役に立つ・シンプル・覚えられる——この3つが揃えば、人は買って、覚えて、友達に教えます。それがビジネスの成長です。」`,
          },
        },
        {
          time: '11:25 – 12:00',
          duration: { en: '35 min', ja: '35分' },
          title: { en: 'Activity — Product Design Sheet & Gallery Walk', ja: 'アクティビティ — 商品デザインシート & ギャラリーウォーク' },
          type: 'activity',
          emoji: '🎨',
          content: {
            en: `Students design the LOOK of their business: name, logo, colours, and slogan.\n\nMATERIALS NEEDED:\n• Business Idea Card (Version 2 from Tuesday)\n• 1 sheet of A4 paper\n• Coloured markers (at least 4 colours)\n\n💡 STAFF TIP: For students who can't think of a logo, ask: "If your business were an animal, what would it be? What colour? What feeling does it represent?" This unlocks creativity.\n\nGALLERY WALK (last 10 min): Spread design sheets on desks. Everyone walks around and gives one comment out loud for a design they like.`,
            ja: `ビジネスアイデアの「見た目」を作ります。名前・ロゴ・色・スローガンを決めましょう。\n\n必要な材料：\n・ ビジネスアイデアカード（バージョン2）\n・ A4白紙1枚\n・ カラーマーカー（最低4色）\n\n💡 スタッフへのアドバイス： ロゴが浮かばない生徒には：「もしビジネスが動物だったら何？色は？どんな気持ちを表す？」と聞いてください。この質問が創造性を引き出します。\n\nギャラリーウォーク（最後の10分）：デザインシートを机に広げ、みんなが歩いて見て回ります。「好きなデザイン」に口頭で一言コメントしましょう。`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 13:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: 'Energizer — 60-Second Logo Sprint', ja: 'エナジャイザー — 60秒ロゴスプリント' },
          type: 'game',
          emoji: '⚡',
          content: {
            en: `PURPOSE: A fast visual thinking warm-up.\n\nHOW TO PLAY:\n1. Staff reads a made-up business name out loud.\n2. Students have 60 seconds to sketch a logo for it.\n3. A few students share — same name, completely different logos!\n\nSuggested names to use:\n• "Okinawa Cloud Bread"\n• "Tiny Surfboard School"\n• "Starfish Delivery"\n• "Moonfish Cafe"\n\nRemind them: there is no "correct" logo. Every version is valid.`,
            ja: `目的：速い視覚的思考を練習するウォームアップ\n\nやり方：\n1. スタッフが架空のビジネス名を読み上げる\n2. 生徒は60秒でそのロゴをスケッチする\n3. 数人が見せ合う——同じ名前でも全く違うロゴが生まれる！\n\n使う名前（例）：\n・「沖縄クラウドブレッド」\n・「タイニーサーフボードスクール」\n・「スターフィッシュ配達」\n・「ムーンフィッシュカフェ」`,
          },
        },
        {
          time: '13:45 – 15:20',
          duration: { en: '95 min', ja: '95分' },
          title: { en: 'Main Activity — Build Your Poster & Prototype', ja: 'メインアクティビティ — ポスターとプロトタイプを作ろう' },
          type: 'activity',
          emoji: '🖼️',
          content: {
            en: `Students create a visual poster for their business AND a physical prototype or model.\n\nPOSTER must include:\n• Business name (large, bold)\n• Logo\n• What it does (one clear sentence)\n• Who it's for\n• The price\n• A slogan\n\nPROTOTYPE: A physical model, mock-up, or drawing of the actual product. If the idea is a service or an app, draw the main screen or describe it visually.\n\n💡 STAFF TIP: Encourage messiness and speed — a rough prototype that exists is better than a perfect one in your head. "Done is better than perfect."`,
            ja: `自分のビジネスのポスターと、プロトタイプ（試作品）を作ります。\n\nポスターに必ず入れるもの：\n・ ビジネス名（大きく、太字で）\n・ ロゴ\n・ 何をするビジネスか（1文で明確に）\n・ 誰のため\n・ 価格\n・ スローガン\n\nプロトタイプ：実際の商品の物理モデル、モックアップ、または絵。アイデアがサービスやアプリの場合は、メイン画面を描くか、見た目を説明してください。\n\n💡 スタッフへのアドバイス： 雑でも速いことを推奨——頭の中の完璧なものより、実際に存在する粗いプロトタイプの方が価値があります。「完璧よりも完成」`,
          },
        },
        {
          time: '15:50 – 16:00',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '⭐ Afternoon Stamp & Day Close', ja: '⭐ 午後スタンプ & 今日のまとめ' },
          type: 'stamp',
          emoji: '⭐',
          content: {
            en: `Give a stamp to everyone who created their poster and prototype.\n\n"Tomorrow is money day. You will price your product, run a mini market, and buy and sell with play money. Get ready!"`,
            ja: `ポスターとプロトタイプを作った全員にスタンプを渡す。\n\n「明日はお金の日！自分の商品に値段をつけて、ミニマーケットで実際に売り買いします。楽しみにしておいてください！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'day3-product-design',
        title: { en: 'MY PRODUCT DESIGN SHEET', ja: '商品デザインシート' },
        emoji: '🎨',
        fields: [
          { type: 'line', label: { en: 'BUSINESS NAME:', ja: 'ビジネス名：' } },
          { type: 'line', label: { en: 'SLOGAN (short catchy phrase):', ja: 'スローガン（短いキャッチフレーズ）：' } },
          { type: 'line', label: { en: 'MAIN COLOURS:', ja: 'メインカラー：' } },
          { type: 'multiline', label: { en: 'WHAT DOES MY BUSINESS DO? (1 clear sentence):', ja: '私のビジネスが解決すること（1文で）：' } },
          { type: 'draw', label: { en: 'MY LOGO:', ja: '私のロゴ：' } },
          { type: 'draw', label: { en: 'MY PRODUCT / SERVICE SKETCH:', ja: '商品・サービスのスケッチ：' } },
        ],
      },
    ],
  },

  // ── DAY 4 ──────────────────────────────────────────────────
  {
    day: 4,
    weekday: { en: 'Thursday', ja: '木曜日' },
    theme: {
      en: 'Money & Market — How Does a Business Make Money?',
      ja: 'お金とマーケット — ビジネスはどうやってお金を稼ぐ？',
    },
    goal: {
      en: 'Students understand cost, price, and profit through games and activities. They price their own product, run a mini market simulation with play money, and refine their pitch for Friday.',
      ja: 'コスト・価格・利益の基本を理解し、自分の商品に価格をつける。ミニマーケットシミュレーションでリアルな売買体験をする。金曜日の発表の準備をする。',
    },
    vocab: [
      {
        word: { en: 'COST', ja: 'コスト' },
        def: { en: 'The money you spend to make or provide your product or service.', ja: '商品やサービスを作るために使うお金' },
        emoji: '💸',
      },
      {
        word: { en: 'PRICE', ja: '価格' },
        def: { en: 'The amount you charge the customer to buy it.', ja: 'お客様に請求する金額' },
        emoji: '🏷️',
      },
      {
        word: { en: 'PROFIT', ja: '利益' },
        def: { en: "What's left over after you subtract cost from price. This is how you earn money.", ja: '価格からコストを引いた残り。これがビジネスで稼ぐお金' },
        emoji: '💰',
      },
      {
        word: { en: 'VALUE', ja: '価値' },
        def: { en: 'How much something is worth to the person buying it — not just the material cost.', ja: '買う人にとってどれだけ大切か——材料費だけじゃない' },
        emoji: '⭐',
      },
      {
        word: { en: 'REVENUE', ja: '売上' },
        def: { en: 'The total money your business receives from all sales.', ja: '全ての売上から入ってくるお金の合計' },
        emoji: '📊',
      },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:52',
          duration: { en: '22 min', ja: '22分' },
          title: { en: 'GAME — The Price Is Right!', ja: 'ゲーム — お値段はいくら？' },
          type: 'game',
          emoji: '🎯',
          content: {
            en: `PURPOSE: Builds intuition about pricing, value, and why things cost what they cost — through fun, competitive guessing.\n\nHOW TO PLAY:\n1. Staff shows a real or drawn product and asks: "How much do you think this costs?"\n2. Each student writes their guess and holds it up.\n3. Staff reveals the real price. The closest guess (without going over) wins a point.\n4. Play 6–8 rounds.\n\nSUGGESTED ROUNDS:\n• Round 1: Small bottle of water at the beach → ~150 yen\n• Round 2: Scoop of gelato at a tourist shop → ~400 yen\n• Round 3: Handmade keyring at a craft market → ~800 yen\n• Round 4: Logo design by a professional → ~5,000–50,000 yen\n• Round 5: Mobile app with 1,000 users → ~500,000+ yen to build\n• Round 6: Can of Okinawa cola at a convenience store → ~150 yen\n\nDEBRIEF:\n"Why did the logo cost so much? It's just a drawing, right?"\nANSWER: "Because the VALUE to the business is huge. Price reflects value, not just materials."`,
            ja: `目的：「なぜこの値段なのか」という感覚を楽しいゲームで養う\n\nやり方：\n1. スタッフが商品を見せて「これいくらだと思う？」と聞く\n2. 各生徒が予想を紙に書いて持ち上げる\n3. スタッフが本当の値段を発表。超えずに一番近い人がポイント獲得\n4. 6〜8ラウンド行う\n\n例題：\n・ ラウンド1：ビーチで買う小さなお水 → 約150円\n・ ラウンド2：観光地のジェラート1スクープ → 約400円\n・ ラウンド3：クラフトマーケットの手作りキーホルダー → 約800円\n・ ラウンド4：プロが作るロゴデザイン → 5,000〜50,000円\n・ ラウンド5：ユーザー1,000人のアプリ開発 → 50万円以上\n・ ラウンド6：コンビニの沖縄コーラ → 約150円\n\nふりかえり：「ロゴはなぜそんなに高いの？ただの絵じゃないの？」\n答え：「ビジネスにとっての価値が大きいから。価格は価値を表す。材料費だけじゃない。」`,
          },
        },
        {
          time: '10:52 – 11:20',
          duration: { en: '28 min', ja: '28分' },
          title: { en: 'Mini Lesson — Cost, Price, and Profit', ja: 'ミニ授業 — コスト・価格・利益' },
          type: 'lesson',
          emoji: '📖',
          content: {
            en: `Write this on the board in LARGE letters — leave it up all day:\n\n💰 PRICE − COST = PROFIT\n\n"Every business has two kinds of money:\n• Money going OUT (cost to make your product)\n• Money coming IN (what customers pay)\nProfit is what's left. Without profit, a business runs out of money and closes."\n\nWorked examples:\n🍋 Handmade lemonade: Cost 50 yen → Sell 150 yen → Profit 100 yen per cup\n☀️ Sunscreen packet: Cost 80 yen → Sell 200 yen → Profit 120 yen each\n🎨 Name stamp kit: Cost 300 yen → Sell 800 yen → Profit 500 yen each\n\n"But if your price is too high, will customers pay? That's why we thought about customers on Tuesday — find a price that works for your customer AND gives you a profit."`,
            ja: `ホワイトボードに大きく書いて、1日中貼り出す：\n\n💰 価格 − コスト = 利益\n\n「ビジネスには2種類のお金がある：\n・ 出ていくお金（商品を作るコスト）\n・ 入ってくるお金（お客様が払う金額）\n利益は残ったもの。利益がなければ、ビジネスはお金が尽きて終わる。」\n\n計算例：\n🍋 手作りレモネード：コスト50円 → 売値150円 → 利益100円\n☀️ 日焼け止めパケット：コスト80円 → 売値200円 → 利益120円\n🎨 ネームスタンプセット：コスト300円 → 売値800円 → 利益500円\n\n「でも値段が高すぎたら、お客様は買う？だから火曜日にお客様のことを考えたんです——お客様が払える価格でかつ利益が出る値段を見つけよう。」`,
          },
        },
        {
          time: '11:20 – 12:00',
          duration: { en: '40 min', ja: '40分' },
          title: { en: 'Activity — Price Your Product + Stamp', ja: 'アクティビティ — 商品に値段をつけよう' },
          type: 'activity',
          emoji: '🏷️',
          content: {
            en: `Students calculate a simple cost and price for their idea.\n\nGuiding questions:\n• "What do you need to buy or make to provide your product?"\n• "How long does it take you? Is your time worth something?"\n• "What would a customer think is a fair price?"\n• "How many would you need to sell to make 10,000 yen?"\n\nAfter worksheets: Ask 3 students to share their price and why. Encourage gentle debate.\n\n⭐ Morning stamp for everyone who completed their Pricing Worksheet!`,
            ja: `生徒がビジネスのコストと値段を計算します。\n\n質問のヒント：\n・「商品を作るために何を買う必要がある？」\n・「どれくらい時間がかかる？時間にも価値がある！」\n・「お客様はどのくらいの値段なら「妥当」と思う？」\n・「10,000円の利益を得るには何個売る必要がある？」\n\nワークシート後：3人に値段と理由を発表させる。\n\n⭐ 価格設定ワークシートを完成させた全員に午前スタンプ！`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 13:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: 'Energizer — Money Math Fast Fire', ja: 'エナジャイザー — 計算早押しゲーム' },
          type: 'game',
          emoji: '⚡',
          content: {
            en: `Staff reads a scenario out loud. Students shout the answer. First correct answer gets a point.\n\n• "You make a sandwich for 200 yen and sell it for 400 yen. Profit?" → 200 yen\n• "Buy stickers for 50 yen, sell at 100 yen, sell 10. Total profit?" → 500 yen\n• "Product costs 300 yen. You want 150 yen profit. What price?" → 450 yen\n• "Sell 5 lemonades at 150 yen. Total cost was 400 yen. Profit?" → 350 yen\n• "Charge 500 yen but costs are 600 yen. Making money or losing?" → Losing 100 yen`,
            ja: `スタッフが問題を読み上げる。最初に正解を叫んだ生徒・グループにポイント。\n\n・「200円のサンドイッチを400円で売った。利益は？」 → 200円\n・「50円のシールを100円で10個売った。合計利益は？」 → 500円\n・「商品コスト300円。150円の利益がほしい。売値は？」 → 450円\n・「レモネード5杯を150円で売った。コストが400円。利益は？」 → 350円\n・「500円で売っているがコストが600円。儲けてる？損してる？」 → 100円の赤字`,
          },
        },
        {
          time: '13:45 – 15:10',
          duration: { en: '85 min', ja: '85分' },
          title: { en: 'Mini Market Simulation', ja: 'ミニマーケットシミュレーション' },
          type: 'activity',
          emoji: '🏪',
          content: {
            en: `The most immersive activity of the week! Students set up their business stall and experience buying and selling in real time.\n\nMATERIALS NEEDED:\n• Play money (print or handwrite on cut paper — 50, 100, 500, 1000 yen)\n• Each student/group starts with 1,500 yen as a customer\n• Business posters and prototype models from Wednesday\n• A price list card for each business\n• A small "cash box" (envelope or small box)\n\nMARKET STRUCTURE:\n• Round 1 (25 min): Half run their stalls, half shop\n• Switch (2 min)\n• Round 2 (25 min): Groups swap roles\n• Count-up (10 min): Each business counts their cash box total and calculates profit\n\nDEBRIEF QUESTIONS:\n• "Which business made the most profit? Why?"\n• "As a customer, what made you decide to buy from one business over another?"\n• "What would you do differently at your stall?"\n\n💡 STAFF TIP: Walk around as a "customer" yourself and occasionally buy something with play money. Your participation energizes the room.`,
            ja: `今週最も没入感のあるアクティビティ！生徒がビジネス屋台を設置し、リアルタイムで売り買いを体験。\n\n必要な材料：\n・ おもちゃのお金（紙に金額を書いて切る——50円・100円・500円・1000円）\n・ 各生徒・グループはお客様として1,500円スタート\n・ 水曜日のポスターとプロトタイプ\n・ 各ビジネスの価格表カード\n・ 小さな「レジ箱」（封筒や小箱）\n\nマーケットの流れ：\n・ ラウンド1（25分）：半分が屋台を運営、残り半分がお買い物\n・ 交代（2分）\n・ ラウンド2（25分）：役割を交代\n・ 集計（10分）：各ビジネスがレジを数えて利益を計算\n\nふりかえり質問：\n・「一番利益が出たビジネスはどこ？なぜ？」\n・「お客様として、どのビジネスで買おうと決めた理由は？」\n・「屋台でもう一度やるなら何を変える？」`,
          },
        },
        {
          time: '15:10 – 15:50',
          duration: { en: '40 min', ja: '40分' },
          title: { en: 'Pitch Practice for Friday', ja: '金曜日の発表練習' },
          type: 'activity',
          emoji: '🎤',
          content: {
            en: `"Tomorrow is Pitch Day. You have 2 minutes to present your complete business idea. Your pitch needs:\n• The PROBLEM\n• Your SOLUTION\n• Who it's FOR\n• The PRICE\n• Why it's GREAT\n\nPractise your full pitch now. Then do one round pitching to ONE other student for feedback. Feedback rule: 'Tell me one thing that was CLEAR and one thing that was CONFUSING.'"`,
            ja: `「明日はピッチ発表の日。2分で完全なビジネスアイデアを発表します。ピッチに入れるもの：\n・ 問題\n・ 解決策\n・ 誰のため\n・ 価格\n・ なぜ素晴らしいか\n\nまず自分のグループで練習。その後、1人の別の生徒に発表してフィードバックをもらいましょう。フィードバックルール：「わかりやすかったこと1つ」「わかりにくかったこと1つ」を言う。」`,
          },
        },
        {
          time: '15:50 – 16:00',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '⭐ Afternoon Stamp & Day Close', ja: '⭐ 午後スタンプ & 今日のまとめ' },
          type: 'stamp',
          emoji: '⭐',
          content: {
            en: `Stamp for everyone who participated in the Mini Market and completed their Pitch Planner.\n\n"Tomorrow is the BIG day — Pitch Day! You will stand up and present your business to the whole group. Tonight, practise your opening sentence one more time. Bring your confidence!"`,
            ja: `ミニマーケットに参加し、ピッチプランナーを完成させた全員にスタンプを渡す。\n\n「明日はいよいよピッチ発表の日！全員の前でビジネスを発表します。今夜、最初の1文をもう一度練習してきてください。自信を持ってきてね！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'day4-pricing',
        title: { en: 'MY PRICING WORKSHEET', ja: '価格設定ワークシート' },
        emoji: '💰',
        fields: [
          { type: 'line', label: { en: 'Business Name:', ja: 'ビジネス名：' } },
          { type: 'line', label: { en: 'My product or service is:', ja: '商品・サービスの内容：' } },
          { type: 'header', label: { en: 'WHAT DOES IT COST ME TO MAKE ONE?', ja: '1つ作るためにかかるコスト：' } },
          { type: 'line', label: { en: 'Materials / supplies: _____ yen', ja: '材料費：_____ 円' } },
          { type: 'line', label: { en: 'Time and effort (estimate): _____ yen', ja: '時間・手間（見積もり）：_____ 円' } },
          { type: 'line', label: { en: 'Any other costs: _____ yen', ja: 'その他のコスト：_____ 円' } },
          { type: 'line', label: { en: 'TOTAL COST: _____ yen', ja: '合計コスト：_____ 円' } },
          { type: 'line', label: { en: 'MY PRICE (what I charge): _____ yen', ja: '私の価格（お客様への請求額）：_____ 円' } },
          { type: 'line', label: { en: 'MY PROFIT PER SALE: Price _____ − Cost _____ = _____ yen', ja: '1個あたりの利益：価格 _____ − コスト _____ = _____ 円' } },
          { type: 'multiline', label: { en: 'WHY WILL MY CUSTOMER THINK THIS PRICE IS FAIR?', ja: 'なぜお客様はこの価格を「妥当」と思う？：' } },
          { type: 'line', label: { en: 'How many to make 10,000 yen profit? _____ sales', ja: '10,000円の利益を得るには何個売る？_____ 個' } },
        ],
      },
      {
        id: 'day4-pitch-planner',
        title: { en: 'MY PITCH PLANNER', ja: 'ピッチプランナー' },
        emoji: '🎤',
        fields: [
          { type: 'multiline', label: { en: 'THE PROBLEM:', ja: '問題：' } },
          { type: 'multiline', label: { en: 'MY SOLUTION:', ja: '私の解決策：' } },
          { type: 'line', label: { en: 'WHO IT\'S FOR:', ja: '誰のため：' } },
          { type: 'line', label: { en: 'THE PRICE: _____ yen per _____', ja: '価格：_____ 円 / _____' } },
          { type: 'multiline', label: { en: 'WHY IT\'S GREAT:', ja: 'なぜ素晴らしいか：' } },
          { type: 'multiline', label: { en: 'MY OPENING LINE (first sentence to grab attention):', ja: '最初の一言（注目を引く冒頭文）：' } },
        ],
      },
    ],
  },

  // ── DAY 5 ──────────────────────────────────────────────────
  {
    day: 5,
    weekday: { en: 'Friday', ja: '金曜日' },
    theme: {
      en: 'Pitch Day! — Present Your Business to the World',
      ja: 'ピッチ発表の日！ — ビジネスを世界に向けて発表しよう',
    },
    goal: {
      en: 'Every student presents their complete business concept in a 2-minute pitch in front of the group. The week ends with a celebration of every idea and every student. No one loses on Pitch Day.',
      ja: '全員が2分間のピッチで自分のビジネスアイデアを発表する。1週間の成果をみんなでお祝いする。ピッチ発表の日に負けはない——全員が称えられる。',
    },
    vocab: [
      {
        word: { en: 'PITCH', ja: 'ピッチ' },
        def: { en: 'A short, confident explanation of your business idea to persuade someone it\'s worth their attention.', ja: '自分のビジネスアイデアを短く・自信を持って説明し、相手の関心を引くこと' },
        emoji: '🎤',
      },
      {
        word: { en: 'CONFIDENCE', ja: '自信' },
        def: { en: 'Believing in your idea enough to say it out loud in front of others.', ja: '自分のアイデアを信じて、みんなの前で声に出せること' },
        emoji: '💪',
      },
      {
        word: { en: 'FEEDBACK', ja: 'フィードバック' },
        def: { en: 'Helpful thoughts from the audience about what they liked or what questions they have.', ja: '聞いてくれた人からの「よかった点」や「聞きたいこと」' },
        emoji: '💬',
      },
      {
        word: { en: 'AUDIENCE', ja: '聴衆' },
        def: { en: 'The people listening to your pitch — treat them as your potential investors!', ja: 'あなたのピッチを聞く人たち——潜在的な投資家だと思って！' },
        emoji: '👥',
      },
      {
        word: { en: 'CELEBRATE', ja: '称える' },
        def: { en: 'Acknowledge and enjoy the hard work and courage of everyone who shared their idea.', ja: 'アイデアを共有した全員の努力と勇気を認め、喜び合うこと' },
        emoji: '🎉',
      },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:52',
          duration: { en: '22 min', ja: '22分' },
          title: { en: 'Energizer — Superhero Warm-Up', ja: 'エナジャイザー — スーパーヒーローウォームアップ' },
          type: 'game',
          emoji: '🦸',
          content: {
            en: `PURPOSE: Builds physical confidence before the pitch. Posture affects how confident people feel — make it fun!\n\nSTEP 1 — POWER POSE (5 min):\nEveryone stands up. "Stand like a superhero — feet apart, hands on hips, chin up, chest out. Hold it for 30 seconds. Do not laugh." (Everyone laughs. That's fine.)\n\nSTEP 2 — VOICE WARM-UP (7 min):\n• "Repeat after me, louder each time: My idea is GREAT!"\n• "Again, LOUDER: My idea is GREAT!"\n• "One more time, like you mean it: MY IDEA IS GREAT!"\n\nSTEP 3 — SHAKE IT OUT (3 min):\nEveryone shakes arms, legs, neck. Release the nerves.\n\nSTEP 4 — PARTNER HYPE (5 min):\nPair up. Each person says: "You are going to be amazing today." Then swap. Sounds silly — works every time.\n\n"The nerves you feel right now are the same as excitement. Your body cannot tell the difference. Channel it into energy, not fear."`,
            ja: `目的：発表前の身体的な自信を高める。姿勢は自信に影響します——楽しくやりましょう！\n\nステップ1 — パワーポーズ（5分）：\n全員立ち上がる。「スーパーヒーローのように立つ——足を広げ、手を腰に、あごを上げて、胸を張る。30秒キープ。笑わないで。」（みんな笑う。それでいい。）\n\nステップ2 — 声のウォームアップ（7分）：\n・「繰り返して、だんだん大きく：私のアイデアは最高です！」\n・「もう一度、もっと大きく：私のアイデアは最高です！」\n・「本気で：私のアイデアは最高です！！」\n\nステップ3 — シェイクアウト（3分）：\n全員で腕・足・首を振ってほぐす。緊張を解放する。\n\nステップ4 — ペアでエール（5分）：\n2人1組になり「今日のあなたは絶対最高です」と言い合う。\n\n「今感じているドキドキは興奮と同じです。身体には違いがわからない。怖さではなくエネルギーとして使おう。」`,
          },
        },
        {
          time: '10:52 – 11:30',
          duration: { en: '38 min', ja: '38分' },
          title: { en: 'Final Pitch Preparation', ja: '最終ピッチ準備' },
          type: 'activity',
          emoji: '📝',
          content: {
            en: `Students have 30 minutes for final preparation.\n\nRemind them: the pitch needs FIVE things:\n1. 🔴 THE PROBLEM — what problem does your business solve?\n2. 💡 YOUR SOLUTION — what exactly is your product or service?\n3. 👤 WHO IT'S FOR — describe your target customer in one sentence\n4. 💰 THE PRICE — how much does it cost and why is that fair?\n5. ⭐ WHY IT'S GREAT — what makes your idea different or special?\n\nStudents practise their pitch one more time — with their poster and prototype ready for display.\n\n💡 STAFF TIP: Walk around and give each student/group one specific encouragement: mention something concrete and true you've seen them do this week.`,
            ja: `生徒が最終準備をする30分間。\n\nピッチに必要な5つのこと：\n1. 🔴 問題 — あなたのビジネスはどんな問題を解決する？\n2. 💡 解決策 — 商品・サービスは具体的に何？\n3. 👤 誰のため — ターゲット顧客を1文で説明\n4. 💰 価格 — いくらか、なぜその値段が妥当か\n5. ⭐ なぜ素晴らしいか — あなたのアイデアは何が違う・特別？\n\nポスターとプロトタイプを展示する準備をしながら、もう一度ピッチを練習。\n\n💡 スタッフへのアドバイス： 各生徒・グループに1つ具体的な励ましを言いましょう。今週見たことに基づいた、本物の言葉を。`,
          },
        },
        {
          time: '11:30 – 12:00',
          duration: { en: '30 min', ja: '30分' },
          title: { en: '⭐ Morning Stamp & Prep Check', ja: '⭐ 午前スタンプ & 準備確認' },
          type: 'stamp',
          emoji: '⭐',
          content: {
            en: `Morning stamp for everyone who has their pitch and materials ready.\n\nSet up the room during this time: arrange chairs/tables in a "Shark Tank" style — presenters stand at the front, audience sits facing them. Put up a banner if possible.`,
            ja: `ピッチと資料の準備が整った全員に午前スタンプ。\n\nこの時間に部屋をセットアップ：椅子・机を「シャークタンク」スタイルに並べる——発表者が前に立ち、観客が向き合って座る。バナーがあれば飾る。`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 15:20',
          duration: { en: '110 min', ja: '110分' },
          title: { en: '🌟 PITCH DAY — Live Presentations!', ja: '🌟 ピッチ発表！ライブプレゼンテーション' },
          type: 'activity',
          emoji: '🌟',
          content: {
            en: `THE FORMAT:\n• 2 minutes per presenter/group to pitch\n• 1 minute of audience questions + staff feedback after each pitch\n• All posters and prototypes displayed\n• Everyone gets applause — no exceptions\n\nAFTER EACH PITCH, the staff says ONE specific piece of real praise and asks ONE genuine question:\n"I was really impressed by [specific thing]. My question is: [genuine curiosity about the idea]."\n\nAUDIENCE RULES (write on board):\n✅ Listen without talking\n✅ Clap for every presenter\n✅ Ask kind, curious questions — not "that won't work"\n✅ Remember: everyone is brave for standing up\n\n💡 STAFF TIP: If a student forgets what to say, smile and say: "Take a breath. Start with the problem your idea solves." That's always enough to restart them.\n\n🎉 After ALL pitches: "Look around this room. Every single person here had the courage to stand up and share their idea. That is the definition of an entrepreneur. You are all entrepreneurs."`,
            ja: `発表の形式：\n・ 1人/グループ2分のピッチ\n・ ピッチの後に観客の質問＋スタッフのフィードバック1分\n・ 全てのポスターとプロトタイプを展示\n・ 全員が拍手を受ける——例外なし\n\n各ピッチ後に、スタッフが具体的な本物の褒め言葉1つと、純粋な質問1つを言う：\n「[具体的なこと]がとても印象的でした。質問は：[アイデアへの純粋な好奇心].」\n\n聴衆のルール（ボードに書く）：\n✅ 発表中は静かに聞く\n✅ 全ての発表者に拍手\n✅ 優しく好奇心のある質問をする——「それはうまくいかない」はダメ\n✅ 覚えておこう：立って話す全員が勇気がある\n\n💡 スタッフへのアドバイス： 話すことを忘れた生徒には、笑顔で「一息ついて。自分のアイデアが解決する問題から始めて」と言う。それだけで再スタートできます。\n\n🎉 全ピッチ終了後：「この部屋を見てください。ここにいる全員が、立ち上がって自分のアイデアを発表する勇気を持ちました。それがアントレプレナーの定義です。みなさん全員がアントレプレナーです。」`,
          },
        },
        {
          time: '15:20 – 16:00',
          duration: { en: '40 min', ja: '40分' },
          title: { en: '🎉 Celebration & Certificates', ja: '🎉 お祝い & 修了証授与' },
          type: 'stamp',
          emoji: '🏆',
          content: {
            en: `AWARDS CEREMONY:\nAnnounce a special recognition for every student. Every person gets acknowledged — use categories so everyone wins something:\n• Most Creative Idea\n• Most Likely to Actually Build This\n• Best Problem Spotter\n• Most Improved Idea\n• Best Customer Research\n• Best Logo / Brand\n• Most Inspiring Story\n• Best Team Collaboration\n\n⭐ AFTERNOON STAMP for everyone who pitched!\n\n📜 CERTIFICATE TIME: Students who have collected 10 stamps unlock their Certificate of Completion.\n\nClose the week:\n"This week you proved something. You can look at the world, see what's missing, imagine something better, and have the courage to share it. That skill — that is the skill of an entrepreneur. And it is yours, forever. Thank you for an incredible week. 沖縄から世界へ！"`,
            ja: `表彰式：\n全ての生徒に特別な称号を発表する——全員が何かで称えられるカテゴリを使う：\n・ 最もクリエイティブなアイデア賞\n・ 本当に実現しそう賞\n・ 最優秀問題発見賞\n・ 最も成長したアイデア賞\n・ 最優秀お客様調査賞\n・ 最優秀ロゴ・ブランド賞\n・ 最も感動的なストーリー賞\n・ 最優秀チームワーク賞\n\n⭐ 発表した全員に午後スタンプ！\n\n📜 修了証授与：スタンプ10個を集めた生徒は修了証がもらえます。\n\n1週間の締めくくり：\n「今週、みなさんはあることを証明しました。世界を見て、何が足りないかを気づき、もっと良いものを想像し、それを共有する勇気を持つこと。そのスキル——それがアントレプレナーのスキルです。そしてそれは、永遠にみなさんのものです。素晴らしい1週間をありがとう。沖縄から世界へ！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'day5-final-pitch',
        title: { en: 'MY FINAL PITCH CARD', ja: '最終ピッチカード' },
        emoji: '🌟',
        fields: [
          { type: 'line', label: { en: 'BUSINESS NAME:', ja: 'ビジネス名：' } },
          { type: 'line', label: { en: 'SLOGAN:', ja: 'スローガン：' } },
          { type: 'multiline', label: { en: '1. THE PROBLEM I SOLVE:', ja: '1. 私が解決する問題：' } },
          { type: 'multiline', label: { en: '2. MY SOLUTION:', ja: '2. 私の解決策：' } },
          { type: 'line', label: { en: '3. WHO IT\'S FOR:', ja: '3. 誰のため：' } },
          { type: 'line', label: { en: '4. THE PRICE: _____ yen', ja: '4. 価格：_____ 円' } },
          { type: 'multiline', label: { en: '5. WHY IT\'S GREAT:', ja: '5. なぜ素晴らしいか：' } },
          { type: 'multiline', label: { en: 'MY OPENING LINE:', ja: '最初の一言：' } },
          { type: 'draw', label: { en: 'LOGO / PRODUCT SKETCH:', ja: 'ロゴ・商品スケッチ：' } },
        ],
      },
    ],
  },
];

export const DAY_COLORS = [
  'linear-gradient(135deg, #FF6B35, #FFD166)',  // Day 1 — orange/gold
  'linear-gradient(135deg, #06B6D4, #06D6A0)',  // Day 2 — cyan/teal
  'linear-gradient(135deg, #A855F7, #EC4899)',  // Day 3 — purple/pink
  'linear-gradient(135deg, #F59E0B, #EF4444)',  // Day 4 — amber/red
  'linear-gradient(135deg, #10B981, #06B6D4)',  // Day 5 — green/cyan
];

export const ACTIVITY_TYPE_COLORS = {
  game: '#06B6D4',
  lesson: '#A855F7',
  activity: '#FF6B35',
  share: '#10B981',
  stamp: '#F59E0B',
};
